import * as dayjs from 'dayjs';
import { Component, Fragment, ReactNode } from 'react';
import { NavLink, useNavigate, useParams, useSearchParams } from 'react-router';
import { AdminApi } from '../admin-api';
import Header from './Header';
import PageLoading from './PageLoading';
import Sidebar from './Sidebar';

import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';

import 'chart.js/auto';
import { Bar } from 'react-chartjs-2';

import { UserResponseDto } from 'src/admin/user/user.dto';
import { CustomEventPerson } from './CustomCalendar';
import LoadingButton from './LoadingButton';

type ValueByPerson = {
  value: number;
  count: number;
  comission: number;
  person: UserResponseDto | null;
};

type ReportData = {
  count: number;
  totalValue: number;
  totalComission: number;
  valueByPerson: ValueByPerson[];
};

export default function CustomReport(props: any): ReactNode {
  const [searchParams] = useSearchParams();

  return (
    <CustomReportComp
      navigate={useNavigate()}
      params={useParams()}
      searchParams={searchParams}
      {...props}
    />
  );
}

class CustomReportComp extends Component {
  constructor(
    readonly props: Readonly<{
      children?: ReactNode;
      navigate?: any;
      currentUser?: any;
      params?: any;
      searchParams?: URLSearchParams;
    }>,
  ) {
    super(props);
    this.state = {
      isLoading: true,
      isSidebarExpanded: false,
      data: null,
      minDate: dayjs().startOf('month').toISOString(),
      maxDate: dayjs().endOf('month').toISOString(),
      users: [],
      period: 'month',
      selectedUsers: [],
      filteredUsers: [],
      isSubmitting: false,
      isFilterExpanded: false,
      expensesSum: 0,
      expensesSumToPay: 0,
      incomesSum: 0,
      historyData: [],
    };
  }

  state: Readonly<{
    isLoading: boolean;
    isSidebarExpanded: boolean;
    data: ReportData | null;
    minDate: string;
    maxDate: string;
    personId?: number;
    users: UserResponseDto[];
    period: string;
    selectedUsers: CustomEventPerson[];
    filteredUsers: CustomEventPerson[];
    isSubmitting: boolean;
    isFilterExpanded: boolean;
    historyData: any[];
    expensesSum?: number;
    expensesSumToPay?: number;
    incomesSum?: number;
  }>;

  async componentDidMount() {
    const _users = await this.getUsers();

    if (this.props.searchParams) {
      const _start = this.props.searchParams.get('start');
      const _end = this.props.searchParams.get('end');
      const _personId = this.props.searchParams.get('personId');

      this.setState({
        period: this.props.searchParams.get('period') || 'month',
      });

      if (_start) {
        this.setState({
          minDate: _start,
        });
      }

      if (_end) {
        this.setState({
          maxDate: _end,
        });
      }

      if (_personId) {
        const _ids = _personId.split(',').map((i) => parseInt(i));
        const _thisU = _users.filter((u: CustomEventPerson) =>
          _ids.includes(u.id),
        );

        this.setState({
          selectedUsers: _thisU,
          filteredUsers: _thisU,
        });
      }
    }

    this.setState({
      isLoading: true,
    });

    setTimeout(async () => {
      await this.getData();
      await this.getExpensesSum();

      this.setState({
        isLoading: false,
      });
    }, 300);
  }

  updateDates(): { s: string; e: string } {
    if (this.state.period === 'custom')
      return { s: this.state.minDate, e: this.state.maxDate };

    let _s = dayjs();
    let _e = dayjs();

    switch (this.state.period) {
      case 'month':
        _s = _s.startOf('month');
        _e = _e.endOf('month');
        break;
      case 'last_month':
        _s = _s.subtract(1, 'month').startOf('month');
        _e = _e.subtract(1, 'month').endOf('month');
        break;
      case 'next_month':
        _s = _s.add(1, 'month').startOf('month');
        _e = _e.add(1, 'month').endOf('month');
        break;
      case 'week':
        _s = _s.startOf('week');
        _e = _e.endOf('week');
        break;
      case 'last_week':
        _s = _s.subtract(1, 'week').startOf('week');
        _e = _e.subtract(1, 'week').endOf('week');
        break;
      case 'next_week':
        _s = _s.add(1, 'week').startOf('week');
        _e = _e.add(1, 'week').endOf('week');
        break;
      case 'year':
        _s = _s.startOf('year');
        _e = _e.endOf('year');
        break;
      case 'last_year':
        _s = _s.subtract(1, 'year').startOf('year');
        _e = _e.subtract(1, 'year').endOf('year');
        break;
      case 'next_year':
        _s = _s.add(1, 'year').startOf('year');
        _e = _e.add(1, 'year').endOf('year');
        break;
    }

    this.setState({
      minDate: _s.toISOString(),
      maxDate: _e.toISOString(),
    });

    return { s: _s.toISOString(), e: _e.toISOString() };
  }

  filterText(addUsers = true) {
    const _dates = `de ${dayjs(this.state.minDate).format('DD/MM/YYYY')} até ${dayjs(this.state.maxDate).format('DD/MM/YYYY')}`;
    let _users = '';

    if (this.state.filteredUsers.length > 0 && addUsers) {
      _users = `| ${this.state.filteredUsers.map((u) => u.name).join(', ')}`;
    }

    return ` ${_dates} ${_users}`;
  }

  async filter() {
    this.setState({
      isSubmitting: true,
    });

    const _dates = this.updateDates();
    await this.getData(_dates.s, _dates.e);
    await this.getExpensesSum(_dates.s, _dates.e);

    this.setState({
      isSubmitting: false,
      isFilterExpanded: false,
      filteredUsers: [...this.state.selectedUsers],
    });
  }

  async getExpensesSum(start?: string, end?: string, personId?: number) {
    const _s = start ?? this.state.minDate;
    const _e = end ?? this.state.maxDate;
    const _p = (
      personId ?? this.state.selectedUsers.map((u) => u.id).join(',')
    ).toString();

    const _sum = await AdminApi.get(
      `/expenses-sum?s=${_s}&e=${_e}&personId=${_p ?? ''}`,
    );

    const _sumToPay = await AdminApi.get(
      `/expenses-sum?s=${_s}&e=${_e}&personId=${_p ?? ''}&done=false`,
    );

    this.setState({
      expensesSum: _sum,
      expensesSumToPay: _sumToPay,
    });
  }

  async getIncomesSum(start?: string, end?: string, personId?: number) {
    const _s = start ?? this.state.minDate;
    const _e = end ?? this.state.maxDate;
    const _p = (
      personId ?? this.state.selectedUsers.map((u) => u.id).join(',')
    ).toString();

    const _sum = await AdminApi.get(
      `/incomes-sum?s=${_s}&e=${_e}&personId=${_p ?? ''}`,
    );
    this.setState({
      incomesSum: _sum,
    });
  }

  async getData(start?: string, end?: string, personId?: number) {
    const _s = start ?? this.state.minDate;
    const _e = end ?? this.state.maxDate;
    const _p = (
      personId ?? this.state.selectedUsers.map((u) => u.id).join(',')
    ).toString();

    const _d = await AdminApi.get(
      `/report?start=${_s}&end=${_e}&personId=${_p ?? ''}`,
    );

    const _dIncomes = await AdminApi.get(
      `/incomes-as-events?start=${_s}&end=${_e}&personId=${_p ?? ''}`,
    );

    // const _h = await AdminApi.get(`/events-history?year=${dayjs(_s).year()}`);
    const _hIncomes = await AdminApi.get(
      `/incomes-history?year=${dayjs(_s).year()}`,
    );

    this.props.searchParams?.set('period', this.state.period);
    this.props.searchParams?.set('start', _s);
    this.props.searchParams?.set('end', _e);
    this.props.searchParams?.set('personId', _p ?? '');

    this.props.navigate({
      pathname: '/reports',
      search: this.props.searchParams?.toString(),
    });

    if (_d && _dIncomes) {
      _d.count += _dIncomes.count;
      _d.totalValue += _dIncomes.totalValue;
      _d.totalComission += _dIncomes.totalComission;
      _d.valueByPerson = [..._d.valueByPerson, ..._dIncomes.valueByPerson];
    }

    this.setState({
      data: _d,
      historyData: _hIncomes,
    });
  }

  async getUsers() {
    this.setState({
      isLoading: true,
      users: [],
    });

    const _users = await AdminApi.get(`/users?onlyPro=true`);

    this.setState({
      isLoading: false,
      users: _users,
    });

    return _users;
  }

  changeSidebar(expanded: boolean) {
    this.setState({
      isSidebarExpanded: expanded,
    });
  }

  sizeClasses() {
    return this.state.isSidebarExpanded
      ? `col-md-9 col-lg-10`
      : `large-calendar`;
  }

  setPeriod(period: string) {
    this.setState({
      period: period,
    });
  }

  setUser(user: CustomEventPerson) {
    let _newUsers = [];

    if (this.state.selectedUsers.includes(user)) {
      _newUsers = this.state.selectedUsers.filter((s) => s.id !== user.id);
    } else {
      _newUsers = [...this.state.selectedUsers, user];
    }

    this.setState({
      selectedUsers: _newUsers,
    });
  }

  putActiveUser(u: CustomEventPerson) {
    return this.state.selectedUsers.map((a) => a.id).includes(u.id)
      ? 'active'
      : '';
  }

  usersButtons() {
    return (
      <div className="row mt-3">
        <div className="col-md-12">
          <hr />
          {this.state.users.map((u) => (
            <button
              key={u.id}
              type="button"
              className={`btn btn-sm btn-link me-2`}
              style={{
                color: u.calendarColor ?? '#000',
                borderColor: this.state.selectedUsers
                  .map((a) => a.id)
                  .includes(u.id)
                  ? (u.calendarColor ?? undefined)
                  : undefined,
              }}
              onClick={() => this.setUser(u)}
            >
              {u.name}
            </button>
          ))}
        </div>
      </div>
    );
  }

  filters() {
    return (
      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-header">
              <h5 className="card-title">
                <a
                  href="#"
                  className="me-3"
                  onClick={(e) => {
                    e.preventDefault();

                    this.setState({
                      isFilterExpanded: !this.state.isFilterExpanded,
                    });
                  }}
                >
                  <i
                    className={`bi ${this.state.isFilterExpanded ? 'bi-dash' : 'bi-window-plus'}`}
                  ></i>
                </a>
                Filtros
              </h5>
            </div>

            {this.state.isFilterExpanded && (
              <div className="card-body">
                <h6 className="card-subtitle mb-2 text-muted">
                  Escolha o período e a pessoa para filtrar os dados.
                </h6>

                <div className="row mt-3">
                  <div className="col-md-12">
                    <label className="form-label">Período</label>
                    <select
                      onChange={(val) => this.setPeriod(val.target.value)}
                      className="form-select"
                      value={this.state.period}
                    >
                      Período
                      <optgroup label="Mês">
                        <option value="month">Esse mês</option>
                        <option value="last_month">Mês passado</option>
                        <option value="next_month">Próximo mês</option>
                      </optgroup>
                      <optgroup label="Semana">
                        <option value="week">Essa semana</option>
                        <option value="last_week">Semana passada</option>
                        <option value="next_week">Próxima semana</option>
                      </optgroup>
                      <optgroup label="Ano">
                        <option value="year">Esse ano</option>
                        <option value="last_year">Ano passado</option>
                        <option value="next_year">Próximo ano</option>
                      </optgroup>
                      <optgroup label="Personalizado">
                        <option value="custom">Definir período</option>
                      </optgroup>
                    </select>
                  </div>
                  {this.state.period === 'custom' && (
                    <div className="row mt-3">
                      <div className="col-md-3">
                        <label className="form-label">Data inicial</label>
                        <Calendar
                          onChange={(d) =>
                            this.setState({
                              minDate: (
                                (d ?? new Date()) as Date
                              ).toISOString(),
                            })
                          }
                          value={this.state.minDate}
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">Data final</label>
                        <Calendar
                          onChange={(d) =>
                            this.setState({
                              maxDate: (
                                (d ?? new Date()) as Date
                              ).toISOString(),
                            })
                          }
                          value={this.state.maxDate}
                        />
                      </div>
                    </div>
                  )}
                </div>

                {this.usersButtons()}
                <div className="row mt-3">
                  <div className="col-md-12"></div>
                  <hr />
                  <div className="d-grid gap-2">
                    <button
                      type="button"
                      className="btn btn-primary"
                      disabled={this.state.isSubmitting}
                      onClick={() => this.filter()}
                    >
                      {this.state.isSubmitting ? <LoadingButton /> : 'Filtrar'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  emptyState() {
    return (
      <div className="row mt-3">
        <div className="col-md-12">
          <div className="card">
            <div className="card-body">
              <h5 className="card-title">
                Nenhum evento realizado nessa busca
              </h5>
              <h6 className="card-subtitle mb-2 text-muted">
                Modifique o filtro para encontrar os eventos.
              </h6>
            </div>
          </div>
        </div>
      </div>
    );
  }

  formatPrice(price: number) {
    return `R$ ${price.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
    })}`;
  }

  personRender(person: ValueByPerson) {
    return (
      <div
        className="card col-md-3 mt-3 me-3 card-inline"
        key={person.person?.id ?? 0}
      >
        <div className="card-body">
          <div className="card-title">
            <div className="d-flex flex-row align-items-center">
              <div className="me-3">
                <img
                  src={person.person?.profilePhotoUrl || '/assets/img/user.png'}
                  className="img-thumbnail profile-photo-report"
                />
              </div>

              <div>
                <NavLink
                  target="_blank"
                  className="person-name"
                  to={`/people/${person.person?.id}/edit`}
                >
                  <h5> {person.person?.name ?? ''}</h5>
                </NavLink>
              </div>
            </div>
          </div>
          <div className="list-group">
            <div
              className="list-group-item d-flex justify-content-between align-items-start"
              aria-current="true"
            >
              <div className="ms-2 me-auto">Total de receitas</div>
              <span className="badge bg-secondary rounded-pill">
                {person.count}
              </span>
            </div>

            <div
              className="list-group-item d-flex justify-content-between align-items-start"
              aria-current="true"
            >
              <div className="ms-2 me-auto">
                <div className="fw-bold">Valor total</div>
              </div>
              <span className="badge bg-success rounded-pill">
                {this.formatPrice(person.value)}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  chart() {
    return (
      <div className="row mt-3 mb-5">
        <div className="col-md-4">
          <div className="card">
            <div className="card-body">
              <div className="card-title">
                <h5>
                  Histórico mensal - ano {dayjs(this.state.minDate).year()}
                </h5>
              </div>
              <div className="custom-chart-container">
                <Bar
                  height={400}
                  data={{
                    labels:
                      this.state.historyData
                        .filter((a) => a.type === 'monthly')
                        .map((p) => p.unit) ?? [],
                    datasets: [
                      {
                        label: 'Receitas',
                        data:
                          this.state.historyData
                            .filter((a) => a.type === 'monthly')
                            .map((p) => p.value) ?? [],
                      },
                    ],
                  }}
                ></Bar>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card">
            <div className="card-body">
              <div className="card-title">
                <h5>
                  Valores por mês - ano {dayjs(this.state.minDate).year()}
                </h5>
              </div>
              <div className="custom-chart-container">
                <Bar
                  height={400}
                  data={{
                    labels:
                      this.state.historyData
                        .filter((a) => a.type === 'sum_monthly')
                        .map((p) => p.unit) ?? [],
                    datasets: [
                      {
                        label: 'Valor',
                        data:
                          this.state.historyData
                            .filter((a) => a.type === 'sum_monthly')
                            .map((p) => p.value ?? 0) ?? [],
                      },
                    ],
                  }}
                ></Bar>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card">
            <div className="card-body">
              <div className="card-title">
                <h5>Histórico anual - todos os tempos</h5>
              </div>
              <div className="custom-chart-container">
                <Bar
                  height={400}
                  data={{
                    labels:
                      this.state.historyData
                        .filter((a) => a.type === 'yearly')
                        .map((p) => p.unit) ?? [],
                    datasets: [
                      {
                        label: 'Atendimentos',
                        data:
                          this.state.historyData
                            .filter((a) => a.type === 'yearly')
                            .map((p) => p.value) ?? [],
                      },
                    ],
                  }}
                ></Bar>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  getQuerystring() {
    let _q = '';

    _q = `s=${this.state.minDate}&e=${this.state.maxDate}&`;

    if (this.state.period.includes('week')) {
      _q += `v=week`;
    } else if (this.state.period.includes('month')) {
      _q += `v=month`;
    } else if (
      this.state.period === 'custom' &&
      this.state.minDate === this.state.maxDate
    ) {
      _q += `v=agenda`;
    } else {
      _q += `v=month`;
    }

    if (this.state.personId) {
      _q += `&p=${this.state.personId}`;
    }

    return _q;
  }

  getDiff() {
    return (this.state.data?.totalValue ?? 0) - (this.state.expensesSum ?? 0);
  }

  report() {
    return (
      <div className="row mt-3">
        <div className="col-md-12">
          <div className="card">
            <div className="card-body">
              <div className="card-title">
                Para ver os eventos realizados, clique{' '}
                <a
                  target="_blank"
                  href={`/admin/calendar?${this.getQuerystring()}`}
                >
                  aqui
                </a>
                <div className="list-group mt-3">
                  <div
                    className="list-group-item d-flex justify-content-between align-items-start"
                    aria-current="true"
                  >
                    <div className="ms-2 me-auto">Total de Receitas</div>
                    <h5>
                      <span className="badge bg-secondary rounded-pill">
                        {this.state.data?.count ?? 0}
                      </span>
                    </h5>
                  </div>

                  <div
                    className="list-group-item d-flex justify-content-between align-items-start"
                    aria-current="true"
                  >
                    <div className="ms-2 me-auto">Valor total</div>
                    <h5>
                      <span className="badge bg-primary rounded-pill">
                        {this.formatPrice(this.state.data?.totalValue ?? 0)}
                      </span>
                    </h5>
                  </div>

                  <div
                    className="list-group-item d-flex justify-content-between align-items-start"
                    aria-current="true"
                  >
                    <div className="ms-2 me-auto">Despesas pagas</div>
                    <h5>
                      <span className="badge bg-danger rounded-pill">
                        {this.formatPrice(this.state.expensesSum ?? 0)}
                      </span>
                    </h5>
                  </div>

                  <div
                    className="list-group-item d-flex justify-content-between align-items-start"
                    aria-current="true"
                  >
                    <div className="ms-2 me-auto">Despesas a pagar</div>
                    <h5>
                      <span className="badge bg-warning rounded-pill">
                        {this.formatPrice(this.state.expensesSumToPay ?? 0)}
                      </span>
                    </h5>
                  </div>

                  <div
                    className="list-group-item d-flex justify-content-between align-items-start"
                    aria-current="true"
                  >
                    <div className="ms-2 me-auto">
                      <div className="fw-bold">Lucro</div>
                    </div>
                    <h5>
                      <span
                        className={`badge ${this.getDiff() >= 0 ? 'bg-success' : 'bg-danger'} rounded-pill`}
                      >
                        {this.formatPrice(this.getDiff())}
                      </span>
                    </h5>
                  </div>

                  <div className="row mt-3">
                    <div className="col-md-12">
                      {(this.state.data?.valueByPerson ?? []).map((p) =>
                        this.personRender(p),
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  render(): ReactNode {
    return this.state.isLoading ? (
      <PageLoading />
    ) : (
      <div>
        <Header
          navigate={this.props.navigate}
          currentUser={this.props.currentUser}
        ></Header>
        <div className="container-fluid">
          <div className="row">
            <Sidebar
              navigate={this.props.navigate}
              currentUser={this.props.currentUser}
              initialExpanded={false}
              onChangeSidebar={(expanded: boolean) =>
                this.changeSidebar(expanded)
              }
            ></Sidebar>
            <main className={`ms-sm-auto px-md-4 ${this.sizeClasses()}`}>
              <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center pt-3 pb-2 mb-3 border-bottom">
                <h1 className="h2">
                  Relatórios
                  <small className="form-text small-text">
                    {this.filterText()}
                  </small>
                </h1>
              </div>
              {this.filters()}
              {!this.state.isSubmitting && !this.state.data?.count ? (
                this.emptyState()
              ) : (
                <Fragment>
                  {this.report()} {this.chart()}
                </Fragment>
              )}
            </main>
          </div>
        </div>
      </div>
    );
  }
}
